import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  AdminSupportingText,
  AdminHeaderAction,
  AdminFormDropdownChevron,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormTwoColGridClass,
  AdminCompactFormPage,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  FILTER_BEHAVIOUR_ROWS,
  KEYWORD_BEHAVIOUR_ROWS,
  LOCATION_BEHAVIOUR_ROWS,
  MESSAGES,
  ORDER_BEHAVIOUR_ROWS,
  RADIUS_OPTIONS,
  RESULT_ORDER_OPTIONS,
  SEARCH_FILTER_OPTIONS,
  SEARCH_MATCHING_ROUTE,
  SEARCH_RULE_AREA_OPTIONS,
  SEARCH_SOURCE_OPTIONS,
  TIMEFRAME_VALUE_OPTIONS,
  buildSampleSearchMatchingRules,
  createDefaultSearchMatchingForm,
  formatSearchMatchingDateTime,
  getRadiusLabel,
  getResultOrderLabel,
  getRuleAreaLabel,
  getSearchFilterLabel,
  getSearchSourceLabel,
  getTimeframeValueLabel,
  normalizeSearchMatchingForm,
} from '../../../data/searchMatchingConfig';
import {
  mergeRulesAfterSave,
  validateSearchMatchingRule,
} from '../../../data/searchMatchingRuleWorkflow';
import { matchesUrlName } from '../../../utils/slugify';

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

const MultiChecklist = ({ label, options, values, onChange, error, required = false, unavailable = [] }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>
      {label} {required ? <span className="text-alertred">*</span> : null}
    </AdminDetailFieldLabel>
    <div className="space-y-2 border border-lightgray rounded-xl px-3 py-3 max-h-56 overflow-y-auto">
      {options.map((option) => {
        const isUnavailable = (unavailable || []).includes(option.value);
        return (
          <label key={option.value} className="flex items-start gap-2 text-sm text-offblack">
            <input
              type="checkbox"
              className="accent-primaryblue mt-0.5 shrink-0"
              checked={(values || []).includes(option.value)}
              onChange={(e) => {
                const next = new Set(values || []);
                if (e.target.checked) next.add(option.value);
                else next.delete(option.value);
                onChange([...next]);
              }}
            />
            <span className="break-words">
              {option.label}
              {isUnavailable ? ' (unavailable)' : ''}
            </span>
          </label>
        );
      })}
    </div>
    {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
  </div>
);

const withUnavailableOptions = (options, values, unavailable, getLabel) => {
  const known = new Set(options.map((o) => o.value));
  const extra = (values || [])
    .filter((v) => !known.has(v))
    .map((v) => ({
      value: v,
      label: `${getLabel(v) || v}${(unavailable || []).includes(v) ? ' (unavailable)' : ''}`,
    }));
  return [...options, ...extra];
};

const SearchMatchingRuleDetail = () => {
  const navigate = useNavigate();
  const { ruleName } = useParams();
  const location = useLocation();
  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isCreate && !isEdit;

  const ruleFromState = location.state?.rule;
  const existingRulesFromState = location.state?.existingRules;
  const allRules = useMemo(
    () => existingRulesFromState ?? buildSampleSearchMatchingRules(),
    [existingRulesFromState],
  );

  const resolvedSource = useMemo(() => {
    if (isCreate) return createDefaultSearchMatchingForm();
    const found =
      (ruleFromState?.id && allRules.find((r) => r.id === ruleFromState.id))
      || allRules.find((r) => matchesUrlName(r.ruleName, ruleName))
      || ruleFromState;
    return found ? normalizeSearchMatchingForm(found) : createDefaultSearchMatchingForm();
  }, [isCreate, ruleFromState, ruleName, allRules]);

  const [formData, setFormData] = useState(resolvedSource);
  const [openedUpdatedOn] = useState(resolvedSource.updatedOn || resolvedSource.lastUpdatedOn || '');
  const [errors, setErrors] = useState({});
  const [rulesSnapshot, setRulesSnapshot] = useState(allRules);

  useEffect(() => {
    setFormData(resolvedSource);
    setRulesSnapshot(allRules);
  }, [resolvedSource, allRules]);

  const clearFieldError = (...keys) => {
    setErrors((prev) => {
      const next = { ...prev };
      keys.forEach((key) => delete next[key]);
      delete next.form;
      return next;
    });
  };

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    clearFieldError(key);
  };

  const handleRuleAreaChange = (nextArea) => {
    setFormData((prev) => ({
      ...createDefaultSearchMatchingForm(),
      ruleName: prev.ruleName,
      ruleArea: nextArea,
      unavailableChoices: {},
    }));
    clearFieldError(
      'ruleArea',
      'searchSources',
      'availableRadius',
      'defaultRadius',
      'availableFilters',
      'timeframeValues',
      'availableOrders',
      'defaultOrder',
    );
  };

  const handleAvailableRadiusChange = (next) => {
    setFormData((prev) => ({
      ...prev,
      availableRadius: next,
      defaultRadius: next.includes(prev.defaultRadius) ? prev.defaultRadius : '',
    }));
    clearFieldError('availableRadius', 'defaultRadius');
  };

  const handleAvailableFiltersChange = (next) => {
    setFormData((prev) => {
      const hadTimeframe = (prev.availableFilters || []).includes('timeframe');
      const hasTimeframe = next.includes('timeframe');
      return {
        ...prev,
        availableFilters: next,
        timeframeValues: hasTimeframe
          ? hadTimeframe
            ? prev.timeframeValues || []
            : []
          : [],
      };
    });
    clearFieldError('availableFilters', 'timeframeValues');
  };

  const handleAvailableOrdersChange = (next) => {
    setFormData((prev) => ({
      ...prev,
      availableOrders: next,
      defaultOrder: next.includes(prev.defaultOrder) ? prev.defaultOrder : '',
    }));
    clearFieldError('availableOrders', 'defaultOrder');
  };

  const validationMessages = useMemo(() => {
    const list = [];
    if (errors.form) list.push(errors.form);
    Object.keys(errors)
      .filter((key) => key !== 'form')
      .forEach((key) => {
        if (errors[key]) list.push(errors[key]);
      });
    return [...new Set(list.filter(Boolean))];
  }, [errors]);

  const handleSave = (e) => {
    e?.preventDefault?.();
    const validation = validateSearchMatchingRule(
      formData,
      rulesSnapshot,
      formData.id || undefined,
    );
    if (Object.keys(validation).length) {
      setErrors(validation);
      return;
    }

    const result = mergeRulesAfterSave(rulesSnapshot, formData, {
      isEdit,
      existingId: formData.id,
      openedUpdatedOn,
    });

    if (!result.ok) {
      setErrors({ form: result.message || MESSAGES.staleEdit });
      return;
    }

    navigate(SEARCH_MATCHING_ROUTE, {
      state: { rules: result.rules, feedback: result.message },
    });
  };

  const pageHeading = isCreate
    ? 'Add Search & Matching Rule'
    : isEdit
      ? 'Edit Search & Matching Rule'
      : 'Search & Matching Rule Detail';

  const unavailable = formData.unavailableChoices || {};
  const area = formData.ruleArea;

  const searchSourceOptions = withUnavailableOptions(
    SEARCH_SOURCE_OPTIONS,
    formData.searchSources,
    unavailable.searchSources,
    getSearchSourceLabel,
  );
  const radiusOptions = withUnavailableOptions(
    RADIUS_OPTIONS,
    formData.availableRadius,
    unavailable.availableRadius,
    getRadiusLabel,
  );
  const filterOptions = withUnavailableOptions(
    SEARCH_FILTER_OPTIONS,
    formData.availableFilters,
    unavailable.availableFilters,
    getSearchFilterLabel,
  );
  const timeframeOptions = withUnavailableOptions(
    TIMEFRAME_VALUE_OPTIONS,
    formData.timeframeValues,
    unavailable.timeframeValues,
    getTimeframeValueLabel,
  );
  const orderOptions = withUnavailableOptions(
    RESULT_ORDER_OPTIONS,
    formData.availableOrders,
    unavailable.availableOrders,
    getResultOrderLabel,
  );

  const defaultRadiusOptions = (() => {
    const selected = RADIUS_OPTIONS.filter((o) => (formData.availableRadius || []).includes(o.value));
    if (
      formData.defaultRadius
      && !selected.some((o) => o.value === formData.defaultRadius)
    ) {
      return [
        ...selected,
        {
          value: formData.defaultRadius,
          label: `${getRadiusLabel(formData.defaultRadius)}${
            (unavailable.defaultRadius || []).includes(formData.defaultRadius) ? ' (unavailable)' : ''
          }`,
        },
      ];
    }
    return selected.map((o) => ({
      ...o,
      label: `${o.label}${
        (unavailable.defaultRadius || []).includes(o.value) ? ' (unavailable)' : ''
      }`,
    }));
  })();

  const defaultOrderOptions = (() => {
    const selected = RESULT_ORDER_OPTIONS.filter((o) => (formData.availableOrders || []).includes(o.value));
    if (
      formData.defaultOrder
      && !selected.some((o) => o.value === formData.defaultOrder)
    ) {
      return [
        ...selected,
        {
          value: formData.defaultOrder,
          label: `${getResultOrderLabel(formData.defaultOrder)}${
            (unavailable.defaultOrder || []).includes(formData.defaultOrder) ? ' (unavailable)' : ''
          }`,
        },
      ];
    }
    return selected.map((o) => ({
      ...o,
      label: `${o.label}${
        (unavailable.defaultOrder || []).includes(o.value) ? ' (unavailable)' : ''
      }`,
    }));
  })();

  const renderViewSettings = () => {
    if (area === 'keyword_search') {
      return (
        <>
          <TextValue
            label="Search Sources"
            value={(formData.searchSources || [])
              .map((v) => {
                const label = getSearchSourceLabel(v);
                return (unavailable.searchSources || []).includes(v)
                  ? `${label} (unavailable)`
                  : label;
              })
              .join(', ') || '—'}
          />
          {KEYWORD_BEHAVIOUR_ROWS.map((row) => (
            <TextValue key={row.key} label={row.label} value={row.value} />
          ))}
        </>
      );
    }
    if (area === 'location_radius') {
      return (
        <>
          <TextValue
            label="Available Radius"
            value={(formData.availableRadius || [])
              .map((v) => {
                const label = getRadiusLabel(v);
                return (unavailable.availableRadius || []).includes(v)
                  ? `${label} (unavailable)`
                  : label;
              })
              .join(', ') || '—'}
          />
          <TextValue
            label="Default Radius"
            value={
              formData.defaultRadius
                ? `${getRadiusLabel(formData.defaultRadius)}${
                    (unavailable.defaultRadius || []).includes(formData.defaultRadius)
                      ? ' (unavailable)'
                      : ''
                  }`
                : '—'
            }
          />
          {LOCATION_BEHAVIOUR_ROWS.map((row) => (
            <TextValue key={row.key} label={row.label} value={row.value} />
          ))}
        </>
      );
    }
    if (area === 'search_filters') {
      const hasTimeframe = (formData.availableFilters || []).includes('timeframe');
      return (
        <>
          <TextValue
            label="Available Filters"
            value={(formData.availableFilters || [])
              .map((v) => {
                const label = getSearchFilterLabel(v);
                return (unavailable.availableFilters || []).includes(v)
                  ? `${label} (unavailable)`
                  : label;
              })
              .join(', ') || '—'}
          />
          {hasTimeframe ? (
            <>
              <TextValue label="Any time" value="Always offered; initial selection (fixed)" />
              <TextValue
                label="Timeframe Values"
                value={(formData.timeframeValues || [])
                  .map((v) => {
                    const label = getTimeframeValueLabel(v);
                    return (unavailable.timeframeValues || []).includes(v)
                      ? `${label} (unavailable)`
                      : label;
                  })
                  .join(', ') || '—'}
              />
            </>
          ) : null}
          {FILTER_BEHAVIOUR_ROWS.map((row) => (
            <TextValue key={row.key} label={row.label} value={row.value} />
          ))}
        </>
      );
    }
    if (area === 'search_result_order') {
      return (
        <>
          <TextValue
            label="Available Orders"
            value={(formData.availableOrders || [])
              .map((v) => {
                const label = getResultOrderLabel(v);
                return (unavailable.availableOrders || []).includes(v)
                  ? `${label} (unavailable)`
                  : label;
              })
              .join(', ') || '—'}
          />
          <TextValue
            label="Default Order"
            value={
              formData.defaultOrder
                ? `${getResultOrderLabel(formData.defaultOrder)}${
                    (unavailable.defaultOrder || []).includes(formData.defaultOrder)
                      ? ' (unavailable)'
                      : ''
                  }`
                : '—'
            }
          />
          {ORDER_BEHAVIOUR_ROWS.map((row) => (
            <TextValue key={row.key} label={row.label} value={row.value} />
          ))}
        </>
      );
    }
    return <TextValue label="Rule Settings" value="Select a Rule Area to see settings." />;
  };

  const renderEditSettings = () => {
    if (!area) {
      return (
        <AdminSupportingText className="text-mediumgray">
          Select a Rule Area to configure settings.
        </AdminSupportingText>
      );
    }

    if (area === 'keyword_search') {
      return (
        <div className="space-y-4 w-full min-w-0">
          <MultiChecklist
            label="Search Sources"
            options={searchSourceOptions}
            values={formData.searchSources}
            onChange={(next) => updateField('searchSources', next)}
            error={errors.searchSources}
            required
            unavailable={unavailable.searchSources}
          />
          {KEYWORD_BEHAVIOUR_ROWS.map((row) => (
            <AdminConfigLockedField key={row.key} label={row.label} value={row.value} isView={false} />
          ))}
        </div>
      );
    }

    if (area === 'location_radius') {
      return (
        <div className="space-y-4 w-full min-w-0">
          <MultiChecklist
            label="Available Radius"
            options={radiusOptions}
            values={formData.availableRadius}
            onChange={handleAvailableRadiusChange}
            error={errors.availableRadius}
            required
            unavailable={unavailable.availableRadius}
          />
          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Default Radius <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <div className="relative w-full min-w-0">
              <select
                className={adminFormDetailSelectClasses(false, Boolean(errors.defaultRadius))}
                value={formData.defaultRadius || ''}
                onChange={(e) => updateField('defaultRadius', e.target.value)}
              >
                <option value="">Select Default Radius</option>
                {defaultRadiusOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
            {errors.defaultRadius ? <AdminFormFieldError>{errors.defaultRadius}</AdminFormFieldError> : null}
          </div>
          {LOCATION_BEHAVIOUR_ROWS.map((row) => (
            <AdminConfigLockedField key={row.key} label={row.label} value={row.value} isView={false} />
          ))}
        </div>
      );
    }

    if (area === 'search_filters') {
      const hasTimeframe = (formData.availableFilters || []).includes('timeframe');
      return (
        <div className="space-y-4 w-full min-w-0">
          <MultiChecklist
            label="Available Filters"
            options={filterOptions}
            values={formData.availableFilters}
            onChange={handleAvailableFiltersChange}
            error={errors.availableFilters}
            required
            unavailable={unavailable.availableFilters}
          />
          {hasTimeframe ? (
            <>
              <AdminConfigLockedField
                label="Any time"
                value="Always offered; initial selection (fixed)"
                isView={false}
              />
              <MultiChecklist
                label="Timeframe Values"
                options={timeframeOptions}
                values={formData.timeframeValues}
                onChange={(next) => updateField('timeframeValues', next)}
                error={errors.timeframeValues}
                required
                unavailable={unavailable.timeframeValues}
              />
            </>
          ) : null}
          {FILTER_BEHAVIOUR_ROWS.map((row) => (
            <AdminConfigLockedField key={row.key} label={row.label} value={row.value} isView={false} />
          ))}
        </div>
      );
    }

    if (area === 'search_result_order') {
      return (
        <div className="space-y-4 w-full min-w-0">
          <MultiChecklist
            label="Available Orders"
            options={orderOptions}
            values={formData.availableOrders}
            onChange={handleAvailableOrdersChange}
            error={errors.availableOrders}
            required
            unavailable={unavailable.availableOrders}
          />
          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Default Order <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <div className="relative w-full min-w-0">
              <select
                className={adminFormDetailSelectClasses(false, Boolean(errors.defaultOrder))}
                value={formData.defaultOrder || ''}
                onChange={(e) => updateField('defaultOrder', e.target.value)}
              >
                <option value="">Select Default Order</option>
                {defaultOrderOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
            {errors.defaultOrder ? <AdminFormFieldError>{errors.defaultOrder}</AdminFormFieldError> : null}
          </div>
          {ORDER_BEHAVIOUR_ROWS.map((row) => (
            <AdminConfigLockedField key={row.key} label={row.label} value={row.value} isView={false} />
          ))}
        </div>
      );
    }

    return null;
  };

  if (isView) {
    return (
      <AdminLayout>
        <div className="min-w-0">
          <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>{pageHeading}</AdminPageTitle>
          <DashboardMainContainer className={`${ADMIN_FORM_PANEL_CLASS} max-w-full min-w-0`}>
            <div className="space-y-8 w-full min-w-0">
              <section className="space-y-4">
                <AdminPanelTitle className="text-offblack !mb-0">Rule Details</AdminPanelTitle>
                <div className={adminFormTwoColGridClass}>
                  <TextValue label="Rule ID" value={formData.id} mono />
                  <TextValue label="Rule Name" value={formData.ruleName} />
                </div>
              </section>

              <section className="space-y-4">
                <AdminPanelTitle className="text-offblack !mb-0">Rule Scope</AdminPanelTitle>
                <TextValue label="Rule Area" value={getRuleAreaLabel(formData.ruleArea)} />
                <TextValue label="Conditions" value="No Conditions" />
              </section>

              <section className="space-y-4">
                <AdminPanelTitle className="text-offblack !mb-0">Rule Settings</AdminPanelTitle>
                {renderViewSettings()}
              </section>

              <section className="space-y-4">
                <AdminPanelTitle className="text-offblack !mb-0">Record Metadata</AdminPanelTitle>
                <div className={adminFormTwoColGridClass}>
                  <TextValue
                    label="Date Added"
                    value={formatSearchMatchingDateTime(formData.createdOn)}
                  />
                  <TextValue label="Added By" value={formData.createdBy || '—'} />
                  <TextValue
                    label="Last Updated"
                    value={formatSearchMatchingDateTime(formData.lastUpdatedOn || formData.updatedOn)}
                  />
                  <TextValue
                    label="Last Updated By"
                    value={formData.lastUpdatedBy || formData.updatedBy || '—'}
                  />
                </div>
              </section>
            </div>
          </DashboardMainContainer>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage title={pageHeading} onSubmit={handleSave} showSave={false}>
        <div className="space-y-4 w-full min-w-0">
          <AdminPanelTitle className="text-offblack !mb-0">Rule Details</AdminPanelTitle>
          {isEdit ? (
            <AdminConfigLockedField label="Rule ID" value={formData.id} mono isView={false} />
          ) : null}
          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Rule Name <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <input
              type="text"
              className={adminFormDetailInputClasses(false, Boolean(errors.ruleName))}
              value={formData.ruleName || ''}
              onChange={(e) => updateField('ruleName', e.target.value)}
              placeholder="Enter Rule Name"
              maxLength={100}
            />
            {errors.ruleName ? <AdminFormFieldError>{errors.ruleName}</AdminFormFieldError> : null}
          </div>
        </div>

        <div className="space-y-4 w-full min-w-0">
          <AdminPanelTitle className="text-offblack !mb-0">Rule Scope</AdminPanelTitle>
          {isEdit ? (
            <AdminConfigLockedField
              label="Rule Area"
              value={getRuleAreaLabel(formData.ruleArea)}
              isView={false}
            />
          ) : (
            <div className="space-y-1 w-full min-w-0">
              <AdminDetailFieldLabel>
                Rule Area <span className="text-alertred">*</span>
              </AdminDetailFieldLabel>
              <div className="relative w-full min-w-0">
                <select
                  className={adminFormDetailSelectClasses(false, Boolean(errors.ruleArea))}
                  value={formData.ruleArea || ''}
                  onChange={(e) => handleRuleAreaChange(e.target.value)}
                >
                  <option value="">Select Rule Area</option>
                  {SEARCH_RULE_AREA_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <AdminFormDropdownChevron />
              </div>
              {errors.ruleArea ? <AdminFormFieldError>{errors.ruleArea}</AdminFormFieldError> : null}
            </div>
          )}
          <AdminConfigLockedField label="Conditions" value="No Conditions" isView={false} />
        </div>

        <div className="space-y-4 w-full min-w-0">
          <AdminPanelTitle className="text-offblack !mb-0">Rule Settings</AdminPanelTitle>
          {renderEditSettings()}
        </div>

        {validationMessages.length ? (
          <div className="space-y-2 w-full min-w-0">
            <AdminPanelTitle className="text-offblack !mb-0">Validation Messages</AdminPanelTitle>
            <ul className="space-y-1">
              {validationMessages.map((msg) => (
                <li key={msg}>
                  <AdminFormFieldError>{msg}</AdminFormFieldError>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="flex items-center justify-end">
          <AdminHeaderAction type="submit">Save</AdminHeaderAction>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default SearchMatchingRuleDetail;
